import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Paper,
  Portal,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OpenInFullOutlined } from "@mui/icons-material";

import useStyles from "./styles";

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import DatagridComponent from "../../datagridComponent";
import LineChartComponent from "../../lineComponent";
import ModalChartHistory from "../../modalChartHistory";
import { color } from "@mui/system";
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";
export default function MultiMetricGraphComponent(params) {
  const {
    data,
    dataNow,
    dataCpuNow, // fallback from old implementation
    titleGraph,
    loading = false,
    handleAplicarZoom = () => {},
    isZoomEnabled = false,
    unit = "%",
    // fator de conversão aplicado sobre cada valor antes de renderizar
    valueMultiplier = 1,
    hideProgress = false,
    itemNames = {},
    labelExtractor,
    // Formatador opcional do valor exibido (tabela/eixo/tooltip). Quando
    // ausente, mantém o comportamento padrão (toFixed(2) + unit).
    formatValue = null,
    // Esconde as colunas min/max/avg da tabela (ex.: métricas de estado binário).
    hideStats = false,
    // Domínio/ticks fixos do eixo Y (ex.: sincronismo → [0,1] com 2 rótulos).
    chartDomain = null,
    chartTicks = null,
    enableHistoryModal = true,
  } = params;

  const hasCustomFormat = typeof formatValue === "function";

  const currentDataNow = dataNow || dataCpuNow; // For backward compatibility
  const [formattedData, setFormattedData] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [openModalHistory, setOpenModalHistory] = useState(false);

  const [lineColors, setLineColors] = useState({});
  const theme = useTheme();
  const styles = useStyles(theme);

  const colorPogress = {
    1: theme?.palette.colors.red.medium,
    2: theme?.palette.colors.blue.medium,
    3: theme?.palette.colors.green.light,
    4: theme?.palette.colors.yellow.medium,
  };

  /**
   * Separa a parte numérica da unidade de uma string formatada.
   * Ex.: "695.39 Kbps" → { num: "695.39", unit: "Kbps" }
   *      "159.563"     → { num: "159.563", unit: "" }
   */
  const splitLabel = (str) => {
    if (!str && str !== 0) return { num: "-", unit: "" };
    const s = String(str).trim();
    // Tenta separar número (com ponto/vírgula/traço) da unidade alfabética
    const match = /^([-\d.,]+)\s*([^\d.,\-].*)$/.exec(s);
    if (!match) return { num: s, unit: "" };
    return { num: match[1], unit: match[2].trim() };
  };

  const columnsData = [
    {
      field: "name",
      sortable: false,
      headerName: "",

      renderCell: (params) => {
        let nameDisplay = params.row.name;
        if (params.row.nameExtracted) {
          // Nome descritivo extraído do item Zabbix — mostra direto
        } else if (nameDisplay === "CPU") {
          nameDisplay =
            titleGraph === "CPU" || titleGraph === "RAM"
              ? `Uso de ${titleGraph}`
              : titleGraph;
        } else if (nameDisplay === "RAM") {
          nameDisplay = `Uso de RAM`;
        } else {
          nameDisplay = `${titleGraph} - ${nameDisplay}`;
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: 8,
                flexShrink: 0,
                backgroundColor: params.row.color,
              }}
            />
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nameDisplay}
            </Box>
          </Box>
        );
      },
      width: 180,
      flex: 1,
    },
    {
      field: "usage",
      renderCell: (params) => {
        const value = params.row.usage;

        let barColor = colorPogress[3]; // verde default
        let percentValue = value;

        if (unit === "%") {
          if (value < 60) {
            barColor = colorPogress[3]; // verde
          } else if (value >= 50 && value < 70) {
            barColor = colorPogress[4]; // amarelo
          } else {
            barColor = colorPogress[1]; // vermelho
          }
        } else if (unit === " Cº" || unit === "Cº") {
          if (value < 40) {
            barColor = colorPogress[3];
          } else if (value >= 40 && value < 65) {
            barColor = colorPogress[4];
          } else {
            barColor = colorPogress[1];
          }
          percentValue = (value / 100) * 100; // base on 100 max
          if (percentValue > 100) percentValue = 100;
        } else if (unit === " V" || unit === "v" || unit === "V") {
          if (value > 10.5 && value <= 29.5) {
            barColor = colorPogress[3];
          } else {
            barColor = colorPogress[4];
          }
          percentValue = (value / 60) * 100; // base on 60 max
          if (percentValue > 100) percentValue = 100;
        }

        return (
          <Box sx={styles.containerUsage}>
            {!hideProgress && (
              <LinearProgress
                variant="determinate"
                value={percentValue}
                sx={{
                  ...styles.linearProgress,
                  [`&.${linearProgressClasses.colorPrimary}`]: {
                    backgroundColor: `${theme.palette.colors.gray.dark}2c`,
                  },
                  [`& .${linearProgressClasses.bar}`]: {
                    borderRadius: 5,
                    backgroundColor: barColor,
                  },
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                ...styles.textUsage,
                color: barColor,
              }}
            >
              {formatValue
                ? formatValue(value)
                : `${Number(value).toFixed(2)}${unit}`}
            </Typography>
          </Box>
        );
      },
      headerName: "Atual",
      width: 180,
    },
    // min/max/avg só fazem sentido para métricas numéricas contínuas;
    // hideStats remove essas colunas (ex.: sincronismo é estado binário).
    ...(hideStats
      ? []
      : [
          {
            field: "min",
            renderCell: (params) => {
              const v = params?.row?.min;
              // label completo para o Tooltip
              const fullLabel = formatValue
                ? formatValue(v)
                : `${v?.toFixed(2)}${unit}`;
              // separa número e unidade para exibição
              const { num, unit: unitPart } = formatValue
                ? splitLabel(fullLabel)
                : {
                    // remove zeros à direita desnecessários no caso padrão
                    num: v != null ? String(parseFloat(v.toFixed(2))) : "-",
                    unit,
                  };
              return (
                <Tooltip title={fullLabel} placement="top" arrow>
                  <Box
                    sx={{
                      display: "flex",
                      overflow: "hidden",
                      width: "100%",
                      alignItems: "baseline",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexShrink: 1,
                        minWidth: 0,
                      }}
                    >
                      {num}
                    </Box>
                    {unitPart && (
                      <Box
                        component="span"
                        sx={{ flexShrink: 0, opacity: 0.7, fontSize: "0.75em" }}
                      >
                        {unitPart}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            },
            headerName: "min",
            width: 80,
          },
          {
            field: "max",
            renderCell: (params) => {
              const v = params?.row?.max;
              const fullLabel = formatValue
                ? formatValue(v)
                : `${v?.toFixed(2)}${unit}`;
              const { num, unit: unitPart } = formatValue
                ? splitLabel(fullLabel)
                : {
                    num: v != null ? String(parseFloat(v.toFixed(2))) : "-",
                    unit,
                  };
              return (
                <Tooltip title={fullLabel} placement="top" arrow>
                  <Box
                    sx={{
                      display: "flex",
                      overflow: "hidden",
                      width: "100%",
                      alignItems: "baseline",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexShrink: 1,
                        minWidth: 0,
                      }}
                    >
                      {num}
                    </Box>
                    {unitPart && (
                      <Box
                        component="span"
                        sx={{ flexShrink: 0, opacity: 0.7, fontSize: "0.75em" }}
                      >
                        {unitPart}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            },
            headerName: "max",
            width: 80,
          },
          {
            field: "avg",
            renderCell: (params) => {
              const v = params?.row?.avg;
              const fullLabel = formatValue
                ? formatValue(v)
                : `${v?.toFixed(2)}${unit}`;
              const { num, unit: unitPart } = formatValue
                ? splitLabel(fullLabel)
                : {
                    num: v != null ? String(parseFloat(v.toFixed(2))) : "-",
                    unit,
                  };
              return (
                <Tooltip title={fullLabel} placement="top" arrow>
                  <Box
                    sx={{
                      display: "flex",
                      overflow: "hidden",
                      width: "100%",
                      alignItems: "baseline",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flexShrink: 1,
                        minWidth: 0,
                      }}
                    >
                      {num}
                    </Box>
                    {unitPart && (
                      <Box
                        component="span"
                        sx={{ flexShrink: 0, opacity: 0.7, fontSize: "0.75em" }}
                      >
                        {unitPart}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            },
            headerName: "avg",
            width: 80,
          },
        ]),
  ];

  useEffect(() => {
    const isArrayData = Array.isArray(data);
    const hasData = isArrayData
      ? data.length > 0
      : Object.keys(data || {}).length > 0;

    let history = [];
    let nowMap = {};
    let cores = [];

    if (!hasData) {
      setFormattedData([]);
      setRowsData([]);
      setLineColors({});
      return;
    }

    if (isArrayData) {
      if (data?.[0]?.clock !== undefined) {
        // Formato single-core (array de {clock, value})
        cores = ["CPU"];
        history = data?.map((item) => ({
          date: Number(item?.clock),
          CPU: Number(item?.value) * valueMultiplier,
        }));

        if (currentDataNow) {
          if (currentDataNow?.value !== undefined) {
            nowMap["CPU"] = Number(currentDataNow?.value) * valueMultiplier;
          } else if (
            Array.isArray(currentDataNow) &&
            currentDataNow?.length > 0
          ) {
            nowMap["CPU"] =
              Number(currentDataNow?.[0]?.value) * valueMultiplier;
          } else if (!isNaN(Number(currentDataNow))) {
            // escalar direto — ex: "0.000379" vindo do backend com lastItem:true
            nowMap["CPU"] = Number(currentDataNow) * valueMultiplier;
          }
        }
      } else {
        // Formato legado — não aplica multiplicador para não quebrar comportamento existente
        const lastData = currentDataNow?.[currentDataNow?.length - 1] || {};
        cores = Object.keys(lastData).filter((k) => k !== "date");
        history = data;
        cores.forEach((core) => {
          nowMap[core] =
            lastData[core] !== undefined ? Number(lastData[core]) : 0;
        });
      }
    } else if (data && typeof data === "object") {
      // Formato multi-instância: objeto keyed por item-key (ex: wanLatency[1], wanLatency[2])
      cores = Object.keys(data);
      const timeMap = {};
      cores.forEach((core) => {
        const entries = data?.[core] || [];
        entries.forEach((entry) => {
          const clock = Number(entry?.clock);
          if (!timeMap[clock]) timeMap[clock] = { date: clock };
          timeMap[clock][core] = Number(entry?.value) * valueMultiplier;
        });
      });
      history = Object.values(timeMap).sort((a, b) => a?.date - b?.date);

      const isKeyedObject =
        currentDataNow !== null &&
        typeof currentDataNow === "object" &&
        !Array.isArray(currentDataNow);

      if (isKeyedObject) {
        Object.keys(currentDataNow).forEach((key) => {
          if (currentDataNow?.[key]?.value !== undefined) {
            nowMap[key] =
              Number(currentDataNow?.[key]?.value) * valueMultiplier;
          } else if (
            Array.isArray(currentDataNow?.[key]) &&
            currentDataNow?.[key]?.length > 0
          ) {
            nowMap[key] =
              Number(currentDataNow?.[key]?.[0]?.value) * valueMultiplier;
          } else {
            const directVal = currentDataNow?.[key];
            if (
              directVal !== undefined &&
              directVal !== null &&
              !isNaN(Number(directVal))
            ) {
              nowMap[key] = Number(directVal) * valueMultiplier;
            }
          }
        });
      } else if (history.length > 0) {
        // dataNow é escalar ou não-keyed — usa o último ponto do histórico por core
        // (valueMultiplier já foi aplicado ao construir o history acima)
        const lastEntry = history[history.length - 1];
        cores.forEach((core) => {
          if (lastEntry?.[core] !== undefined) {
            nowMap[core] = lastEntry[core];
          }
        });
      }
    }

    const colorKeys = Object.keys(theme.palette.colors)?.filter(
      (color) => color !== "white" && color !== "black",
    );
    let colors = [];

    const newRows = cores.map((core, index) => {
      const values = history
        ?.map((entry) => entry[core])
        .filter((v) => v !== undefined && !isNaN(v));
      const min = values.length ? Math.min(...values) : 0;
      const max = values.length ? Math.max(...values) : 0;
      const avg = values.length
        ? values.reduce((sum, v) => sum + v, 0) / values.length
        : 0;

      let coreNameDisplay = core;
      let nameExtracted = false;
      if (labelExtractor && itemNames?.[core]) {
        const nameMatch = itemNames[core].match(new RegExp(labelExtractor));
        if (nameMatch?.[1]) {
          coreNameDisplay = nameMatch[1].trim();
          nameExtracted = true;
        }
      }
      if (!nameExtracted) {
        const match = core.match(/\[(.*?)\]/);
        if (match) {
          coreNameDisplay = match[1];
        } else if (core.startsWith("core")) {
          coreNameDisplay = core.replace("core", "");
        }
      }

      const colorName = colorKeys[index % colorKeys?.length];
      const colorValue = theme.palette.colors[colorName].medium;
      colors.push(colorValue);

      return {
        id: index + 1,
        name: coreNameDisplay,
        coreKey: core,
        min,
        max,
        avg,
        color: colorValue,
        usage: nowMap[core] || 0,
        nameExtracted,
      };
    });

    setFormattedData(history);
    setRowsData(newRows);
    setLineColors(
      Object.fromEntries(newRows.map((row) => [row.coreKey, row.color])),
    );
  }, [data, currentDataNow, theme, unit, valueMultiplier]);

  const formatMaxValue = (value) => {
    if (formatValue) return formatValue(value);
    return `${Number(value).toFixed(1)}${unit}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    const date = data.date;

    const entries = Object.entries(data)
      .filter(([key]) => key !== "date")
      .map(([core, value]) => ({ core, value }))
      .sort((a, b) => b?.value - a?.value);

    const topThree = entries?.slice(0, 3);
    const extraCount = entries?.length - 3;

    return (
      <Box sx={styles.boxContainerTooltip}>
        <Typography fontSize={"14px"}>
          {`Data: ${dayjs.unix(date).format("DD/MM/YY HH:mm")}`}
        </Typography>

        {topThree.map(({ core, value }) => {
          const color = payload?.find((p) => p?.dataKey === core)?.color;
          const numericValue = Number(value);
          let coreNameDisplay = core;
          let nameExtracted = false;
          if (labelExtractor && itemNames?.[core]) {
            const nameMatch = itemNames[core].match(new RegExp(labelExtractor));
            if (nameMatch?.[1]) {
              coreNameDisplay = nameMatch[1].trim();
              nameExtracted = true;
            }
          }
          if (!nameExtracted) {
            const match = core.match(/\[(.*?)\]/);
            if (match) {
              coreNameDisplay = match[1];
            } else if (core.startsWith("core")) {
              coreNameDisplay = core.replace("core", "");
            }
          }

          return (
            <Box key={core} sx={styles.containerTooltip}>
              <Box
                sx={{
                  ...styles.boxValueTooltip,
                  backgroundColor: color,
                }}
              />
              <Typography sx={styles.textTooltip}>
                {nameExtracted
                  ? `${coreNameDisplay}: `
                  : coreNameDisplay === "CPU" || coreNameDisplay === "RAM"
                    ? `${titleGraph}: `
                    : `${titleGraph} - ${coreNameDisplay}: `}
              </Typography>
              <Typography>
                {isNaN(numericValue)
                  ? "N/A"
                  : formatValue
                    ? formatValue(numericValue)
                    : `${numericValue.toFixed(2)}${unit}`}
              </Typography>
            </Box>
          );
        })}

        {extraCount > 0 && (
          <Typography sx={styles.textInfoTooltip}>
            {`Mais ${extraCount} ite${extraCount > 1 ? "ns" : "m"}...`}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Paper
      elevation={theme.palette.mode == "dark" ? 0 : 3}
      sx={{
        ...styles.paper,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {loading && <LinearProgress sx={styles.linearProgressLoading} />}
      <Box sx={styles.containerComponent}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={styles.cardSubText}>{titleGraph}</Typography>
          {enableHistoryModal && (
            <Tooltip title="Abrir histórico ampliado">
              <IconButton
                size="small"
                onClick={() => setOpenModalHistory(true)}
                sx={{ color: theme.palette.text.secondary }}
              >
                <OpenInFullOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={styles.containerGrid}>
          <Grid
            container
            spacing={1}
            columns={{ xs: 1, sm: 1, md: 6, lg: 6, xl: 4, xxl: 3 }}
          >
            <Grid item xs={1} sm={1} md={6} lg={6} xl={4} xxl={3}>
              <Box width="100%" height={120}>
                <LineChartComponent
                  data={formattedData}
                  isZoomEnabled={isZoomEnabled}
                  onZoomChange={(range) => {
                    const startDate = dayjs.unix(range.start).tz(timeZone);
                    const endDate = dayjs.unix(range.end).tz(timeZone);

                    handleAplicarZoom(startDate, endDate);
                  }}
                  maxTicks={10}
                  xOptions={{
                    dataKey: "date",
                    tickFormatter: (clock) =>
                      dayjs.unix(clock).tz(timeZone).format("DD/MM HH:mm"),
                  }}
                  maxValuebps={unit === "%" ? 100 : undefined}
                  yOptions={{
                    tickFormatter: formatMaxValue,
              
                    ...(hasCustomFormat && {
                      domain: chartDomain ?? [0, "auto"],
                      width: chartTicks ? 100 : 70,
                      ...(chartTicks && { ticks: chartTicks }),
                    }),
                  }}
                  
                  {...(hasCustomFormat && {
                    lineChartOptions: {
                      margin: { top: 10, left: 0, bottom: 0 },
                    },
                  })}
                  customTooltip={CustomTooltip}
                  mainLineColor={
                    theme.palette.mode == "dark"
                      ? theme.palette.white.main
                      : theme.palette.colors.gray.darker
                  }
                  objectColorsLine={lineColors}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Paper variant="outlined" sx={styles.paperDatagrid} elevation={0}>
          <DatagridComponent rows={rowsData} columns={columnsData} />
        </Paper>
      </Box>

      {enableHistoryModal && (
        <ModalChartHistory
          open={openModalHistory}
          onClose={() => setOpenModalHistory(false)}
          title={titleGraph}
          chartType="multiLine"
          initialData={data}
          currentValue={dataNow}
          unit={unit}
          formatValue={formatValue}
          valueMultiplier={valueMultiplier}
          itemNames={itemNames}
          labelExtractor={labelExtractor}
        />
      )}
    </Paper>
  );
}
